import React, { FC, Fragment } from 'react';
import { Box, IconButton, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RemoveIcon from '@mui/icons-material/Remove';
import { User } from 'models/user';
import { LinkedUser, LinkedClinic } from 'models/order';
import { Organization } from 'models/organization';
import InfoText from 'components/shared/info-text.component';

type ParticipantsListProps = {
    linkedDentists: LinkedUser[];
    linkedClinics: LinkedClinic[];
    customEmails: string[];
    onRemoveDentist?: (dentist: User) => void;
    onRemoveClinic?: (clinic: Organization) => void;
    showRemoveButton?: boolean;
};
const ParticipantsList: FC<ParticipantsListProps> = ({
    linkedDentists,
    linkedClinics,
    customEmails,
    onRemoveDentist,
    onRemoveClinic,
    showRemoveButton = false,
}: ParticipantsListProps) => {
    const { t: translate } = useTranslation('orders');

    const onRemoveDentistClick = (dentist: User) => {
        if (onRemoveDentist) {
            onRemoveDentist(dentist);
        }
    };

    const onRemoveClinicClick = (clinic: Organization) => {
        if (onRemoveClinic) {
            onRemoveClinic(clinic);
        }
    };

    return (
        <Fragment>
            {linkedDentists.length > 0 ? (
                <Box mb={2}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                        {translate('orders:PARTICIPANT_USERS')}
                    </Typography>
                    <Paper variant="outlined">
                        <List dense>
                            {linkedDentists.map((dentist) => (
                                <ListItem
                                    key={dentist.user._id}
                                    secondaryAction={
                                        showRemoveButton && (
                                            <IconButton
                                                edge="end"
                                                onClick={() => {
                                                    onRemoveDentistClick(dentist.user);
                                                }}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemText>
                                        {dentist.user.fullName}
                                        <Typography variant="body2" color="grey.700">
                                            {dentist.user.email}
                                        </Typography>
                                    </ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Box>
            ) : undefined}
            {linkedClinics.length > 0 ? (
                <Box mb={2}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                        {translate('orders:PARTICIPANT_CLINICS')}
                    </Typography>
                    <Paper variant="outlined" sx={{ width: '100%' }}>
                        <List dense>
                            {linkedClinics.map((linkedClinic) => (
                                <ListItem
                                    key={linkedClinic.clinic?._id}
                                    secondaryAction={
                                        showRemoveButton && (
                                            <IconButton
                                                edge="end"
                                                onClick={() => {
                                                    onRemoveClinicClick(linkedClinic.clinic);
                                                }}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemText>
                                        {linkedClinic.clinic?.name}
                                        <Typography variant="body2" color="grey.700">
                                            {linkedClinic.clinic?.contact?.communication?.email}
                                        </Typography>
                                    </ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Box>
            ) : undefined}
            {customEmails?.length > 0 ? (
                <Box>
                    <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                        {translate('orders:CUSTOM_PARTICIPANT_EMAILS')}
                    </Typography>
                    <Paper variant="outlined">
                        <List dense>
                            {customEmails?.map((email: string) => (
                                <ListItem key={email}>
                                    <ListItemText>{email}</ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Box>
            ) : undefined}
            {!linkedDentists.length && !linkedClinics.length && !customEmails.length ? (
                <InfoText text={translate('THIS_ORDER_HAVE_NO_PARTICIPANTS')} />
            ) : undefined}
        </Fragment>
    );
};

export default ParticipantsList;
